import Vue from 'vue'
import { omit } from 'lodash'
import embedSlideApi from '@/api/embed-slide'

const _state = {
  embedSlides: {},
  loading: false,
  error: null,
  embedSrcError: null,
}

// The embed endpoint silently drops keys outside its own schema, so fullSlide is
// persisted through the generic slide-attributes API and read back here. Falsey
// leaves the slide object untouched, keeping a plain embed slide's load identical.
const FULL_SLIDE_ATTRIBUTE_KEY = 'fullSlide'
const EMBED_SLIDE_TYPE = 'embed'

const _getters = {
  activeEmbedSlide: (state, _, rootState) => {
    const { activeSlideId } = rootState
    return {
      ...rootState.slideActive,
      ...state.embedSlides[activeSlideId],
    }
  },
  // True only while the active slide is an embed slide with Full slide on. The
  // presenter reads it to give a full-slide embed the same full-canvas treatment
  // a native full-screen plugin slide gets — hide the top join bar and pin the
  // footer over the embed — so it reaches edge-to-edge instead of leaving white
  // bands top and bottom.
  isActiveEmbedFullSlide: (state, getters, rootState, rootGetters) => {
    if (!rootGetters['featureToggles/canUseEmbedFullSlide']) return false
    if (rootState.slideActive?.type !== EMBED_SLIDE_TYPE) return false
    return Boolean(getters.activeEmbedSlide?.attributes?.[FULL_SLIDE_ATTRIBUTE_KEY])
  },
  embedSrcError: (state) => state.embedSrcError,
  embedPlatform: (_, getters) => {
    const embedSrc = getters.activeEmbedSlide?.attributes?.embedSrc

    if (!embedSrc) return null

    if (embedSrc.includes('youtube.com') || embedSrc.includes('youtu.be')) {
      return 'youtube'
    }
    if (embedSrc.includes('docs.google.com/presentation')) {
      return 'googleSlides'
    }
    if (embedSrc.includes('miro.com')) {
      return 'miro'
    }
    if (embedSrc.includes('skoletube.dk')) {
      return 'skoletube'
    }
    return 'other'
  },
}

// A rapid re-toggle fires two attribute writes back to back; with no ordering the
// earlier POST can resolve after the later one, leaving the server on a stale value
// that a later reload flips the checkbox back to (a lost update). Serialise writes
// per slide so they land in toggle order — last toggle wins — and track the latest
// toggle so only its failure reverts the optimistic state; an older failed write
// must not clobber a newer toggle already applied on top of it.
const fullSlideWriteChains = new Map()
const latestFullSlideWriteSeqs = new Map()

const mergeFullSlideAttribute = async (dispatch, slideId, slide) => {
  if (!slide || typeof slide !== 'object') return slide
  let fullSlide = false
  try {
    const attributes = await dispatch('getSlideAttributesWithCacheAction', slideId, { root: true })
    fullSlide = Boolean(attributes?.[FULL_SLIDE_ATTRIBUTE_KEY])
  } catch (error) {
    fullSlide = false
  }
  if (!fullSlide) return slide
  return {
    ...slide,
    attributes: { ...slide.attributes, [FULL_SLIDE_ATTRIBUTE_KEY]: true },
  }
}

const actions = {
  async loadEmbedSlideAction({ commit, dispatch, rootGetters }, { slideId }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      commit('SET_EMBED_SRC_ERROR', null)
      const response = await embedSlideApi.getEmbedSlide(slideId)
      const slide = rootGetters['featureToggles/canUseEmbedFullSlide']
        ? await mergeFullSlideAttribute(dispatch, slideId, response.body)
        : response.body
      commit('SET_CURRENT_EMBED_SLIDE', slide)
      return slide
    } catch (error) {
      commit('SET_ERROR', error.message)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateEmbedSlideAction({ state, commit, getters }, {
    slideId, attributes,
  }) {
    try {
      commit('SET_LOADING', true)
      commit('SET_EMBED_SRC_ERROR', null)
      const updatedAttributes = {
        ...getters.activeEmbedSlide?.attributes,
        ...attributes,
      }

      // Optimistically update the state
      const optimisticSlide = {
        slideId,
        attributes: updatedAttributes,
      }
      commit('SET_CURRENT_EMBED_SLIDE', optimisticSlide)

      const response = await embedSlideApi.updateEmbedSlide(slideId, omit(updatedAttributes, FULL_SLIDE_ATTRIBUTE_KEY))
      // The embed endpoint doesn't round-trip fullSlide (it lives in the generic
      // slide-attributes store), so an unrelated embed edit would otherwise wipe
      // the full-slide layout from local state until the next reload. Read the
      // value at merge time, not before the await, so a Full-slide toggle that
      // lands mid-request isn't reverted by a stale pre-request snapshot; and by
      // slideId, because the presenter may have moved to another slide meanwhile.
      const slide = response.body
      const currentFullSlide = state.embedSlides[slideId]?.attributes?.[FULL_SLIDE_ATTRIBUTE_KEY]
      if (slide?.attributes && currentFullSlide !== undefined) {
        slide.attributes = { ...slide.attributes, [FULL_SLIDE_ATTRIBUTE_KEY]: currentFullSlide }
      }
      commit('SET_CURRENT_EMBED_SLIDE', slide)
      return slide
    } catch (resp) {
      if (resp?.body?.errors?.embedSrc) {
        commit('SET_EMBED_SRC_ERROR', resp?.body?.errors?.embedSrc)
      }
      throw resp
    } finally {
      commit('SET_LOADING', false)
    }
  },

  async updateFullSlideAction({
    state, commit, getters, dispatch,
  }, { slideId, fullSlide }) {
    // Persist through the generic slide-attributes API so the embed endpoint
    // needs no schema change; reflect locally at once so the toggle and the
    // canvas layout update live without waiting on the round-trip.
    const previousAttributes = getters.activeEmbedSlide?.attributes
    commit('SET_CURRENT_EMBED_SLIDE', {
      slideId,
      attributes: { ...previousAttributes, [FULL_SLIDE_ATTRIBUTE_KEY]: fullSlide },
    })

    const seq = (latestFullSlideWriteSeqs.get(slideId) || 0) + 1
    latestFullSlideWriteSeqs.set(slideId, seq)

    const write = async () => {
      try {
        await dispatch('upsertSlideAttributeAction', {
          slideId,
          attributeKey: FULL_SLIDE_ATTRIBUTE_KEY,
          attributeValue: fullSlide,
        }, { root: true })
      } catch (error) {
        // Revert only when this is still the last toggle, so the checkbox tracks
        // what actually persisted instead of silently sticking on a value the
        // server rejected. A superseded write leaves the newer state in place, and
        // only fullSlide is restored so an embed edit saved meanwhile survives.
        if (latestFullSlideWriteSeqs.get(slideId) === seq) {
          commit('SET_CURRENT_EMBED_SLIDE', {
            slideId,
            attributes: {
              ...state.embedSlides[slideId]?.attributes,
              [FULL_SLIDE_ATTRIBUTE_KEY]: previousAttributes?.[FULL_SLIDE_ATTRIBUTE_KEY],
            },
          })
        }
      }
    }

    const pending = (fullSlideWriteChains.get(slideId) || Promise.resolve()).then(write)
    fullSlideWriteChains.set(slideId, pending)
    return pending
  },

  setEmbedSrcErrorAction({ commit }, error) {
    commit('SET_EMBED_SRC_ERROR', error)
  },
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  SET_EMBED_SRC_ERROR(state, error) {
    state.embedSrcError = error
  },

  SET_CURRENT_EMBED_SLIDE(state, slide) {
    if (slide && slide.slideId) {
      Vue.set(state.embedSlides, slide.slideId, slide)
    }
  },

  REMOVE_EMBED_SLIDE(state, slideId) {
    const { [slideId]: removed, ...remaining } = state.embedSlides
    state.embedSlides = remaining
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
